import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Filter, Search } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * The combined Filters panel — Court/Round/Group/Player/Status/My Games/
 * Search all in one picker, committed together via one "Apply Filters"
 * button (a staged draft, same pattern as CourtsSettingsModal/
 * MatchSettingsModal: this modal stays mounted between opens and re-seeds
 * its draft from whatever's actually applied each time it opens, rather
 * than showing a stale one from last time). Every section is independently
 * optional — whichever ones the schedule has nothing to offer (no courts
 * configured, no group labels, ...) just don't render, same convention the
 * rest of this module already uses for the Court column / MLP Settings
 * button / Team Pairing section.
 *
 * "Search by name" is My Games' counterpart for someone watching on
 * somebody ELSE's behalf (a parent looking for their daughter's matches, a
 * coach for one of their players) — always a debounced backend lookup (see
 * useScheduleBuilder.js's `searchUsers`, reusing the same `GET /user?search=`
 * endpoint the Squads "Add/Invite Members" flow already uses — same
 * 4-character minimum convention) against REAL registered users, so two
 * people who happen to share a name can be told apart (results show email
 * alongside name). One real API regardless of standalone Explore Formats vs.
 * a real tournament — Explore Formats' mock data has this session's real
 * user id planted on a few entries specifically so a real search still finds
 * someone to match against there (see mockParticipants.json). Picking a
 * result sets `watchedUserId` — filtered the exact same way My Games is
 * (see scheduleRows.js's `involvedUserIds`), just against a chosen id
 * instead of the logged-in one.
 *
 * `options` — `{ rounds: [label, ...], groups: [label, ...],
 * participants: [{id, name}, ...] }` (see useScheduleBuilder.js's
 * `filterOptions`, built from the WHOLE schedule via
 * tieFormats.getScheduleRoundsArrays so every section offers every value
 * that exists anywhere in it, not just whichever list/tab happens to be on
 * screen). `courts` is the organizer's own configured court/field list.
 * `myGamesAvailable` gates the My Games toggle — only true in a real
 * tournament where the logged-in user maps to an actual participant (see
 * useScheduleBuilder.js's `currentUserId`); Explore Formats has no real
 * identity behind its mock data, so that section never renders there.
 */
const FiltersModal = ({
  isOpen,
  onClose,
  filters,
  options,
  courts = [],
  myGamesAvailable,
  searchUsers,
  onApply,
}) => {
  const { theme } = useTheme();
  const [draft, setDraft] = useState(filters);
  const [playerSearch, setPlayerSearch] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setDraft(filters);
    setPlayerSearch('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const filteredParticipants = useMemo(() => {
    const q = playerSearch.trim().toLowerCase();
    if (!q) return options.participants;
    return options.participants.filter((p) => p.name.toLowerCase().includes(q));
  }, [options.participants, playerSearch]);

  if (!isOpen) return null;

  const toggleValue = (field, value) => {
    setDraft((prev) => {
      const list = prev[field] || [];
      const next = list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
      return { ...prev, [field]: next };
    });
  };

  const apply = () => {
    onApply(draft);
    onClose();
  };

  const clearAll = () => {
    setDraft({
      courts: [],
      rounds: [],
      groups: [],
      participants: [],
      statuses: [],
      myGamesOnly: false,
      watchedUserId: null,
      watchedUserName: '',
    });
  };

  return createPortal(
    // z-[56]: above FullscreenScheduleOverlay's z-[55] — this modal can be
    // opened from inside fullscreen (see its own Filters button), so it must
    // outrank the overlay or it paints invisibly underneath it. Same layer as
    // the Start/Complete Tournament confirm dialogs, below MatchDetailModal's
    // z-[60] (see GeneratedScheduleStep.jsx's layering doc comment).
    <div className="fixed inset-0 z-[56] flex items-center justify-center p-4" onClick={(e) => e.stopPropagation()}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative flex max-h-[90vh] w-full max-w-lg flex-col rounded-2xl border ${theme.border} ${theme.card} ${theme.shadow}`}>
        <div className={`flex shrink-0 items-center justify-between border-b ${theme.border} px-6 py-4`}>
          <div className={`flex items-center gap-2 text-base font-bold ${theme.text}`}>
            <Filter size={18} />
            Filters
          </div>
          <button
            type="button"
            onClick={onClose}
            className={`flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${theme.buttonGhost}`}
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto px-6 py-4">
          {myGamesAvailable && (
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={draft.myGamesOnly}
                onChange={(e) => setDraft((prev) => ({ ...prev, myGamesOnly: e.target.checked }))}
                className="h-4 w-4 rounded text-blue-600"
              />
              <span className={`text-sm font-medium ${theme.text}`}>My Games only</span>
            </label>
          )}

          <div>
            <div className={`mb-2 text-sm font-medium ${theme.text}`}>Search by name</div>
            <PersonSearchSection
              theme={theme}
              searchUsers={searchUsers}
              watchedUserId={draft.watchedUserId}
              watchedUserName={draft.watchedUserName}
              onSelect={(person) => setDraft((prev) => ({ ...prev, watchedUserId: person.id, watchedUserName: person.name }))}
              onClear={() => setDraft((prev) => ({ ...prev, watchedUserId: null, watchedUserName: '' }))}
            />
            <p className={`mt-1 text-xs ${theme.textMuted}`}>
              Looks up real registered users by name (4+ characters) — pick the exact person, even if someone else shares their name.
            </p>
          </div>

          {courts.length > 0 && (
            <CheckboxSection
              label="Court / Field"
              theme={theme}
              options={courts.map((c) => ({ value: c.label, label: c.label }))}
              selected={draft.courts}
              onToggle={(value) => toggleValue('courts', value)}
            />
          )}

          {options.rounds.length > 0 && (
            <CheckboxSection
              label="Round"
              theme={theme}
              options={options.rounds.map((label) => ({ value: label, label }))}
              selected={draft.rounds}
              onToggle={(value) => toggleValue('rounds', value)}
            />
          )}

          {options.groups.length > 0 && (
            <CheckboxSection
              label="Group"
              theme={theme}
              options={options.groups.map((label) => ({ value: label, label }))}
              selected={draft.groups}
              onToggle={(value) => toggleValue('groups', value)}
            />
          )}

          {options.participants.length > 0 && (
            <div>
              <div className={`mb-2 text-sm font-medium ${theme.text}`}>Player / Team</div>
              {options.participants.length > 8 && (
                <div className="relative mb-2">
                  <Search size={13} className={`pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 ${theme.textMuted}`} />
                  <input
                    type="text"
                    value={playerSearch}
                    onChange={(e) => setPlayerSearch(e.target.value)}
                    placeholder="Search..."
                    className={`w-full rounded-lg border py-1.5 pl-8 pr-3 text-sm ${theme.input}`}
                  />
                </div>
              )}
              <div className="max-h-40 space-y-1 overflow-y-auto">
                {filteredParticipants.length === 0 ? (
                  <p className={`text-xs italic ${theme.textMuted}`}>No matches</p>
                ) : (
                  filteredParticipants.map((p) => (
                    <label key={p.id} className="flex cursor-pointer items-center gap-2">
                      <input
                        type="checkbox"
                        checked={draft.participants.includes(p.id)}
                        onChange={() => toggleValue('participants', p.id)}
                        className="h-4 w-4 rounded text-blue-600"
                      />
                      <span className={`truncate text-sm ${theme.text}`}>{p.name}</span>
                    </label>
                  ))
                )}
              </div>
            </div>
          )}

          <div>
            <div className={`mb-2 text-sm font-medium ${theme.text}`}>Status</div>
            <CheckboxSection
              label={null}
              theme={theme}
              options={[{ value: 'draft', label: 'Draft' }]}
              selected={draft.statuses}
              onToggle={(value) => toggleValue('statuses', value)}
            />
            <p className={`mt-1 text-xs ${theme.textMuted}`}>More statuses will appear once live scoring begins.</p>
          </div>
        </div>

        <div className={`flex shrink-0 items-center gap-2 border-t ${theme.border} px-6 py-4`}>
          <button
            type="button"
            onClick={clearAll}
            className={`rounded-xl border px-4 py-2.5 text-sm font-semibold transition-colors ${theme.border} ${theme.buttonGhost}`}
          >
            Clear all
          </button>
          <button
            type="button"
            onClick={apply}
            className={`flex-1 rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors ${theme.buttonPrimary} hover:opacity-90 active:scale-[0.99]`}
          >
            Apply Filters
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

const CheckboxSection = ({ label, theme, options, selected, onToggle }) => (
  <div>
    {label && <div className={`mb-2 text-sm font-medium ${theme.text}`}>{label}</div>}
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const active = selected.includes(opt.value);
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onToggle(opt.value)}
            className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
              active ? `border-transparent ${theme.buttonPrimary}` : `${theme.border} ${theme.buttonGhost}`
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  </div>
);

// The backend-driven person picker — debounces keystrokes and only actually
// calls `searchUsers` once the query hits 4 characters (same minimum
// AddInviteMembers.jsx already uses for this same endpoint), so short
// prefixes ("Jo", "Pri") never fire a request. Once a result is picked, this
// collapses to a single "selected person" chip instead of the input — there
// is exactly one person being watched at a time, not a multi-select list.
const PersonSearchSection = ({ theme, searchUsers, watchedUserId, watchedUserName, onSelect, onClear }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (query.trim().length < 4) {
      setResults([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(() => {
      searchUsers(query)
        .then((found) => {
          if (!cancelled) setResults(found);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, searchUsers]);

  if (watchedUserId) {
    return (
      <div className={`flex items-center justify-between gap-2 rounded-lg border px-3 py-2 ${theme.border}`}>
        <span className={`truncate text-sm font-medium ${theme.text}`}>{watchedUserName}</span>
        <button
          type="button"
          onClick={onClear}
          aria-label="Clear selected person"
          className={`shrink-0 rounded p-1 transition-colors ${theme.buttonGhost}`}
        >
          <X size={14} />
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="relative">
        <Search size={13} className={`pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 ${theme.textMuted}`} />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Enter at least 4 characters to search..."
          className={`w-full rounded-lg border py-1.5 pl-8 pr-3 text-sm ${theme.input}`}
        />
      </div>
      {query.trim().length > 0 && query.trim().length < 4 && (
        <p className={`mt-1 text-xs ${theme.textMuted}`}>Keep typing — 4 characters minimum.</p>
      )}
      {loading && <p className={`mt-1 text-xs ${theme.textMuted}`}>Searching…</p>}
      {!loading && query.trim().length >= 4 && (
        <div className="mt-2 max-h-40 space-y-1 overflow-y-auto">
          {results.length === 0 ? (
            <p className={`text-xs italic ${theme.textMuted}`}>No users found</p>
          ) : (
            results.map((person) => (
              <button
                key={person.id}
                type="button"
                onClick={() => onSelect(person)}
                className={`flex w-full flex-col items-start rounded-lg px-2 py-1.5 text-left transition-colors ${theme.buttonGhost}`}
              >
                <span className={`text-sm font-medium ${theme.text}`}>{person.name}</span>
                {person.email && <span className={`text-xs ${theme.textMuted}`}>{person.email}</span>}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default FiltersModal;
